import {
  DEFAULT_PARTY_OPTIONS,
  DEFAULT_SETTINGS,
  gameSettingsSchema,
  partyOptionsSchema,
  playerProfileSchema,
  randomAvatar,
  type GameSettings,
  type PartyOptions,
  type PlayerProfile,
} from '@okwhere/shared';
import { randomId } from './browser';

const PROFILE_KEY = 'okwhere:profile';
const SETTINGS_KEY = 'okwhere:settings';
const PARTY_OPTIONS_KEY = 'okwhere:party-options';
const TOKEN_KEY = 'okwhere:token';
const ACCESS_KEY = 'okwhere:access';

export function readJson(storage: Storage, key: string): unknown {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeJson(storage: Storage, key: string, value: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or storage disabled: the app still works, it just won't remember.
  }
}

export function loadProfile(): PlayerProfile | null {
  const parsed = playerProfileSchema.safeParse(readJson(localStorage, PROFILE_KEY));
  return parsed.success ? parsed.data : null;
}

export function saveProfile(profile: PlayerProfile): void {
  writeJson(localStorage, PROFILE_KEY, profile);
}

// Picked once per page load, so a player without a saved profile keeps the same avatar everywhere.
const fallbackAvatar = randomAvatar();

export function draftProfile(): PlayerProfile {
  return loadProfile() ?? { name: '', avatar: fallbackAvatar };
}

export function loadSettings(): GameSettings {
  const parsed = gameSettingsSchema.safeParse(readJson(localStorage, SETTINGS_KEY));
  return parsed.success ? parsed.data : DEFAULT_SETTINGS;
}

export function saveSettings(settings: GameSettings): void {
  writeJson(localStorage, SETTINGS_KEY, settings);
}

/** The party-only options a host last used (speed bonus, teams). */
export function loadPartyOptions(): PartyOptions {
  const parsed = partyOptionsSchema.safeParse(readJson(localStorage, PARTY_OPTIONS_KEY));
  return parsed.success ? parsed.data : DEFAULT_PARTY_OPTIONS;
}

export function savePartyOptions(options: PartyOptions): void {
  writeJson(localStorage, PARTY_OPTIONS_KEY, options);
}

function newToken(): string {
  const token = randomId();
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage unavailable: the token lives for this page only.
  }
  return token;
}

function storedToken(): string {
  try {
    return sessionStorage.getItem(TOKEN_KEY) ?? newToken();
  } catch {
    return newToken();
  }
}

/**
 * Identifies this browser tab to party rooms so a refresh puts the player back in their seat.
 * Kept per tab (sessionStorage) so several tabs can join the same party as different players.
 */
let token = storedToken();

// "Duplicate tab" copies sessionStorage, so two open tabs could share a token and fight over one
// seat. Each tab announces its token on load; a tab already using it answers, and the newcomer
// picks a fresh one.
const tabId = randomId();
const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('okwhere:tabs');
channel?.addEventListener('message', (event: MessageEvent<{ type: string; token: string; tabId: string }>) => {
  const message = event.data;
  if (message.token !== token || message.tabId === tabId) return;
  if (message.type === 'hello') channel.postMessage({ type: 'taken', token, tabId });
  else if (message.type === 'taken') token = newToken();
});
channel?.postMessage({ type: 'hello', token, tabId });

/** Resolves once other tabs had a chance to report a duplicated token. */
export const playerTokenReady: Promise<void> = new Promise((resolve) => setTimeout(resolve, channel ? 150 : 0));

export function playerToken(): string {
  return token;
}

/** Token received for an access code; it lets this browser start games. */
function storedAccess(): string | null {
  const value = readJson(localStorage, ACCESS_KEY);
  return typeof value === 'string' ? value : null;
}

let access = storedAccess();
const accessListeners = new Set<() => void>();

export function accessToken(): string | null {
  return access;
}

export function setAccessToken(value: string | null): void {
  access = value;
  if (value) writeJson(localStorage, ACCESS_KEY, value);
  else {
    try {
      localStorage.removeItem(ACCESS_KEY);
    } catch {
      // Storage unavailable: forgetting it for this page is enough.
    }
  }
  for (const listener of accessListeners) listener();
}

// Unlocking (or being signed out) in one tab applies to the others too.
window.addEventListener('storage', (event) => {
  if (event.key !== ACCESS_KEY && event.key !== null) return;
  access = storedAccess();
  for (const listener of accessListeners) listener();
});

export function subscribeAccessToken(listener: () => void): () => void {
  accessListeners.add(listener);
  return () => accessListeners.delete(listener);
}
